import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, FileText } from 'lucide-react';
import { useProjects } from '../hooks/useProject';
import { useAuth } from '../contexts/AuthContext';
import AdminLogin from '../components/auth/AdminLogin';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import { formatDate } from '../utils/formatters';

export default function HomePage() {
  const { projects, loading, error } = useProjects();
  const { isAdminAuthenticated } = useAuth();

  // Check admin authentication first
  if (!isAdminAuthenticated) {
    return <AdminLogin />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-secondary-600">Chargement des projets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>Réessayer</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header subtitle="Monteur Vidéo Freelance" />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="section-padding bg-gradient-to-br from-primary-600 to-primary-800 text-white">
          <div className="container mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                Cahiers de Préconisation
                <br />
                <span className="text-yellow-300">Post-Production Vidéo</span>
              </h1>
              <p className="text-xl md:text-2xl mb-8 text-white/90">
                Propositions commerciales détaillées et sur mesure pour vos projets documentaires
              </p>
              <div className="flex flex-wrap gap-8 justify-center text-left max-w-2xl mx-auto">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <FileText size={24} />
                  </div>
                  <div>
                    <div className="font-bold text-lg">Transparence totale</div>
                    <div className="text-sm text-white/80">Tarifs clairs et détaillés</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                    <Calendar size={24} />
                  </div>
                  <div>
                    <div className="font-bold text-lg">Planning précis</div>
                    <div className="text-sm text-white/80">Jalons et livrables</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Projects List */}
        <section className="section-padding bg-secondary-50">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl font-bold mb-4">Mes Propositions</h2>
              <p className="text-xl text-secondary-600">
                {projects.filter(p => !p.archived).length} projet{projects.filter(p => !p.archived).length > 1 ? 's' : ''} disponible
                {projects.filter(p => !p.archived).length > 1 ? 's' : ''}
                {projects.filter(p => p.archived).length > 0 && ` • ${projects.filter(p => p.archived).length} archivé${projects.filter(p => p.archived).length > 1 ? 's' : ''}`}
              </p>
            </motion.div>

            {projects.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-secondary-600 text-lg">Aucun projet disponible pour le moment.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                {projects.map((project, index) => (
                  <motion.div
                    key={project.slug}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link to={`/projet/${project.slug}`}>
                      <Card hover className="h-full">
                        {/* Image */}
                        {project.branding?.heroImage && (
                          <div
                            className="h-48 bg-cover bg-center rounded-t-xl mb-4 -m-6 mb-4"
                            style={{
                              backgroundImage: `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${project.branding.heroImage})`,
                            }}
                          >
                            <div className="h-full flex items-end justify-between p-4">
                              <span className="px-3 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-semibold text-secondary-900">
                                {formatDate(project.createdAt)}
                              </span>
                              {project.archived && (
                                <span className="px-3 py-1 bg-orange-500/90 backdrop-blur-sm rounded-full text-xs font-semibold text-white">
                                  Archivé
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Content */}
                        <div>
                          <h3 className="text-2xl font-bold mb-2 line-clamp-2">{project.title}</h3>
                          <p className="text-secondary-600 mb-4 line-clamp-2">{project.subtitle}</p>

                          {/* Meta */}
                          <div className="space-y-2 text-sm text-secondary-600 mb-4">
                            {project.client && (
                              <div className="flex items-center gap-2">
                                <span>🏢</span>
                                <span>{project.client.name}</span>
                              </div>
                            )}
                            {project.stats && (
                              <div className="flex items-center gap-4">
                                <span>⏱ {project.stats.durationMonths} mois</span>
                                <span>🎬 {project.stats.finalDuration}</span>
                              </div>
                            )}
                          </div>

                          {/* CTA */}
                          <Button variant="primary" icon={<ArrowRight size={18} />} className="w-full">
                            Voir la proposition
                          </Button>
                        </div>
                      </Card>
                    </Link>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* About Section */}
        <section className="section-padding bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl font-bold mb-6">Pourquoi des cahiers de préconisation ?</h2>
                <div className="grid md:grid-cols-3 gap-8 text-left mt-12">
                  <div>
                    <div className="text-4xl mb-4">📋</div>
                    <h3 className="font-bold text-xl mb-2">Clarté</h3>
                    <p className="text-secondary-600">
                      Toutes les étapes, livrables et tarifs détaillés en un seul document
                    </p>
                  </div>
                  <div>
                    <div className="text-4xl mb-4">🤝</div>
                    <h3 className="font-bold text-xl mb-2">Confiance</h3>
                    <p className="text-secondary-600">
                      Pas de surprises : vous savez exactement ce que vous obtenez
                    </p>
                  </div>
                  <div>
                    <div className="text-4xl mb-4">⚡</div>
                    <h3 className="font-bold text-xl mb-2">Efficacité</h3>
                    <p className="text-secondary-600">
                      Prenez le temps d'étudier la proposition avant de vous engager
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
